import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Divider,
} from "@mui/material";
import { Autocomplete, LoadScriptNext } from "@react-google-maps/api";

const SplitLegModal = ({ open, handleClose, handleSubmit }) => {
  const [addressData, setAddressData] = useState({
    newCompanyName: "",
    address: "",
    address2: "",
    city: "",
    state: "",
    postal: "",
    country: "",
    latitude: "",
    longitude: "",
  });

  useEffect(() => {
    if (!open) {
      // Reset form when modal closes
      setAddressData({
        newCompanyName: "",
        address: "",
        address2: "",
        city: "",
        state: "",
        postal: "",
        country: "",
        latitude: "",
        longitude: "",
      });
    }
  }, [open]);

  const handlePlaceChanged = (place) => {
    if (place.address_components) {
      const address = {
        newCompanyName: place.name || "",
        address: "",
        address2: "",
        city: "",
        state: "",
        postal: "",
        country: "",
        latitude: place.geometry?.location.lat() || "",
        longitude: place.geometry?.location.lng() || "",
      };

      let streetNumber = "";
      let route = "";
      let subpremise = "";
      let premise = "";

      place.address_components.forEach((component) => {
        const types = component.types;
        if (types.includes("street_number")) {
          streetNumber = component.long_name;
        } else if (types.includes("route")) {
          route = component.long_name;
        } else if (types.includes("subpremise")) {
          subpremise = component.long_name;
        } else if (types.includes("premise")) {
          premise = component.long_name;
        } else if (types.includes("locality")) {
          address.city = component.long_name;
        } else if (types.includes("administrative_area_level_1")) {
          address.state = component.long_name;
        } else if (types.includes("postal_code")) {
          address.postal = component.long_name;
        } else if (types.includes("country")) {
          address.country = component.long_name;
        }
      });

      // Combine address components for address1 and address2
      const fullStreetAddress = `${streetNumber} ${route}`.trim();
      address.address = fullStreetAddress;

      // Use premise/subpremise for address2 if available
      if (subpremise || premise) {
        address.address2 = `${subpremise} ${premise}`.trim();
      } else {
        address.address2 = "";
      }

      setAddressData(address);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAddressData((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = () => {
    handleSubmit(addressData);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Split Leg</DialogTitle>
      <DialogContent>
        <LoadScriptNext
          googleMapsApiKey={import.meta.env.VITE_API_MAP_KEY}
          libraries={["places"]}
        >
          <Grid container spacing={1}>
            <Grid item xs={12} mt={1}>
              <Autocomplete
                onLoad={(autocomplete) => {
                  window.autocompleteSplitLegModalRef = autocomplete;
                }}
                onPlaceChanged={() => {
                  if (window.autocompleteSplitLegModalRef) {
                    const place =
                      window.autocompleteSplitLegModalRef.getPlace();
                    handlePlaceChanged(place);
                  }
                }}
              >
                <TextField
                  fullWidth
                  placeholder="Search Address"
                  variant="outlined"
                />
              </Autocomplete>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ mt: 1 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                fullWidth
                label="Company name"
                name="newCompanyName"
                value={addressData.newCompanyName}
                onChange={handleChange}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                variant="outlined"
                fullWidth
                label="Address"
                name="address"
                value={addressData.address}
                onChange={handleChange}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                variant="outlined"
                fullWidth
                label="Address 2"
                name="address2"
                value={addressData.address2}
                onChange={handleChange}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                variant="outlined"
                fullWidth
                label="City"
                name="city"
                value={addressData.city}
                onChange={handleChange}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                variant="outlined"
                fullWidth
                label="State"
                name="state"
                value={addressData.state}
                onChange={handleChange}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                variant="outlined"
                fullWidth
                label="Postal Code"
                name="postal"
                value={addressData.postal}
                onChange={handleChange}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                variant="outlined"
                fullWidth
                label="Country"
                name="country"
                value={addressData.country}
                onChange={handleChange}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                variant="outlined"
                fullWidth
                label="Latitude"
                name="latitude"
                value={addressData.latitude}
                onChange={handleChange}
                margin="normal"
                InputLabelProps={{ shrink: true }}
                disabled
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                variant="outlined"
                fullWidth
                label="Longitude"
                name="longitude"
                value={addressData.longitude}
                onChange={handleChange}
                margin="normal"
                InputLabelProps={{ shrink: true }}
                disabled
              />
            </Grid>
          </Grid>
        </LoadScriptNext>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={onSubmit} variant="contained">
          Split Leg
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SplitLegModal;
